import { ErrorHandler, inject, Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';

const RETRY_KEY: string = 'chunk-error-retries';
const RELOADING_KEY: string = 'chunk-error-reloading';
const MAX_RETRIES: number = 2;

@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
  private readonly toastController: ToastController = inject(ToastController);
  private readonly defaultHandler: ErrorHandler = Sentry.createErrorHandler();

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error)) {
      this.handleChunkError(error);
      return;
    }

    this.defaultHandler.handleError(error);
  }

  private isChunkLoadError(error: unknown): boolean {
    if (!error) return false;

    const errorObj: Error = (error as { rejection?: Error })?.rejection ?? (error as Error);
    const name: string = errorObj?.name ?? '';
    const message: string = errorObj?.message ?? '';

    return (
      name === 'ChunkLoadError' ||
      message.includes('Loading chunk') ||
      message.includes('Failed to fetch dynamically imported module')
    );
  }

  private async handleChunkError(error: unknown): Promise<void> {
    const retries: number = this.getRetryCount();

    Sentry.captureException(error, {
      tags: { chunkError: true, retryAttempt: retries + 1 },
    });

    if (retries < MAX_RETRIES) {
      this.setRetryCount(retries + 1);
      await this.showToastAndReload();
    } else {
      await this.showFeatureUnavailableToast();
    }
  }

  private async showToastAndReload(): Promise<void> {
    const toast: HTMLIonToastElement = await this.toastController.create({
      message: 'Updating app...',
      duration: 1500,
      position: 'top',
      color: 'primary',
    });
    await toast.present();

    setTimeout(async () => {
      try {
        const cacheNames: string[] = await caches.keys();
        await Promise.all(cacheNames.map((name: string) => caches.delete(name)));

        const registrations: readonly ServiceWorkerRegistration[] =
          await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((reg: ServiceWorkerRegistration) => reg.unregister()),
        );
      } catch {
        // Best-effort cache clear
      }
      sessionStorage.setItem(RELOADING_KEY, 'true');
      this.reloadPage();
    }, 1500);
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  private async showFeatureUnavailableToast(): Promise<void> {
    const toast: HTMLIonToastElement = await this.toastController.create({
      message: 'This feature is temporarily unavailable. Please try again later.',
      duration: 5000,
      position: 'top',
      color: 'warning',
    });
    await toast.present();
  }

  private getRetryCount(): number {
    const value: string | null = sessionStorage.getItem(RETRY_KEY);
    return value ? parseInt(value, 10) : 0;
  }

  private setRetryCount(count: number): void {
    sessionStorage.setItem(RETRY_KEY, count.toString());
  }

  static resetRetryCount(): void {
    if (sessionStorage.getItem(RELOADING_KEY)) {
      sessionStorage.removeItem(RELOADING_KEY);
      return;
    }
    sessionStorage.removeItem(RETRY_KEY);
  }
}
